/* eslint-disable react/jsx-closing-tag-location */
/* eslint-disable no-unused-vars */
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Container, Divider, Grid, IconButton, LinearProgress, Stack, TextField,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { ChevronRightRounded, ExpandLessRounded, ExpandMoreRounded } from '@mui/icons-material';
import _ from 'lodash';
import AlertContext from '../../../../Context/AlertContext';
import Elements from '../../../Elements';
import {
  assignCourier, assignItemsToBox, declineRequest, getAdminRequestDetails, setRequestAsDone,
} from '../../../../Redux/Models/request';
import { getCourier } from '../../../../Redux/Models/courier';
import { getCategories, getConditions } from '../../../../Redux/Models/master';
import { getDigitalBox, resetBoxColumn, setBoxColumn } from '../../../../Redux/Models/box';
import Images from '../../../../Configs/images';
import ConfirmationContext from '../../../../Context/ConfirmationContext';
import ROUTES from '../../../../Configs/routes';
import StepIcon from '../../../../Configs/steps';
import { alphabets } from '../../../../Configs/alphabets';
import { getTotalItems } from '../../../../Utils/helpers';

export default function Dev({
  selectedRequest: req, classes, error, message, isLoading,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { requestId } = useParams();

  const { openAlert } = useContext(AlertContext);
  const { openConfirmation, closeConfirmation } = useContext(ConfirmationContext);
  const [activeStep, setActiveStep] = useState(1);
  const [cancelStep, setCancelStep] = useState(0);
  const [cancelNotes, setCancelNotes] = useState('');

  const { couriers } = useSelector((state) => state.courier);

  const [choosenCourier, setChoosenCourier] = useState({ courier_name: '', id: '' });
  const [hideTrackHistory, setHideTH] = useState(false);
  const [digitalBox, setDigitalBox] = useState([]);

  useEffect(() => {
    // step 4 manually by admin
    const courierOnIt = ['Waiting for Courier Acceptance', 'Courier On The Way', 'Courier Arrived at Pickup Place', 'Pickup Already Confirm by Customer', 'Arrived at Warehouse'];
    if (courierOnIt.indexOf(req.status) > -1) {
      setActiveStep(3);
    } else if (req.status === 'Stored at Warehouse') {
      setHideTH(true);
      setActiveStep(5);
    } else if (req.status === 'Done') {
      setHideTH(true);
      setActiveStep(5);
      openConfirmation({
        title: 'The request process has done',
        desc: 'You can back to request page',
        confirmText: 'Ok, go back',
        cancelText: 'Stay here',
        handleAgree: () => {
          navigate(ROUTES.request());
          closeConfirmation();
        },
        handleClose: () => {
          closeConfirmation();
        },
      });
    } else if (['Admin Canceled', 'Canceled'].includes(req.status)) {
      openConfirmation({
        title: 'The request process has canceled',
        desc: 'You can back to request page',
        confirmText: 'Ok, go back',
        cancelText: 'Stay here',
        handleAgree: () => {
          navigate(ROUTES.request());
          closeConfirmation();
        },
        handleClose: () => {
          closeConfirmation();
        },
      });
    }

    if (req.driver) {
      setChoosenCourier(req.driver);
    }
  }, [req]);

  useEffect(() => {
    if (error) {
      openAlert({
        message: `Something went wrong. ${message}`,
        severity: 'error',
      });
    }
  }, [error]);

  const getInitialData = () => {
    dispatch(getAdminRequestDetails(requestId));
    dispatch(getCourier({}));
    dispatch(getCategories());
    dispatch(getConditions());
  };

  useEffect(() => {
    dispatch(getDigitalBox(req.box?.id ? 'Box' : 'Item'));
  }, [req.box]);

  useEffect(() => {
    if (!requestId) {
      navigate(-1);
    } else {
      getInitialData();
    }
  }, [requestId]);

  const updateStatusAsDone = (status) => {
    dispatch(setRequestAsDone(requestId));
  };

  const handleAssignCourier = async () => {
    await dispatch(assignCourier(requestId, { courier: choosenCourier.id }));
  };

  const handleDecline = async () => {
    await dispatch(declineRequest(requestId, { notes: cancelNotes }));
  };

  const handleAssignItemsToBox = () => {
    if (!digitalBox.length) {
      openAlert({
        message: 'Complete warehouse validation! Please choose storage site!',
        severity: 'error',
      });
      return;
    }
    const body = {
      owner_id: req.user?.id,
      digital_box: digitalBox,
    };

    openConfirmation({
      title: 'Assigning Item to Selected Storage?',
      desc: 'Box items will be assigned to a selected storage',
      confirmText: 'Confirm Assigned',
      handleAgree: async () => {
        dispatch(assignItemsToBox(requestId, body));
        closeConfirmation();
      },
      handleClose: () => {
        closeConfirmation();
      },
      direction: 'row-reverse',
      maxWidth: 'xs',
    });
  };

  const handleDigitalBox = (aisles = []) => {
    const itemIds = req.items?.map((item) => item.id) ?? [];
    const boxIds = req.box?.map((b) => ({ id: b.id, type: b.type })) ?? [];

    let remainingItems = [...itemIds];
    let remainingBoxes = [...boxIds];

    const assignedAisles = aisles.map((aisle) => {
      let availableQuota = aisle.original_slot ?? 0;
      let assignedBoxes = [];
      let assignedItems = [];

      // Assign boxes first
      if (remainingBoxes.length > 0) {
        let useQuota = 6;
        for (let index = 0; index < remainingBoxes.length; index += 1) {
          const element = remainingBoxes[index];
          if (element?.type === '1x3' && availableQuota >= 3) useQuota = 3;
          if (element?.type === '2x6' && availableQuota >= 12) useQuota = 12;
        }
        const boxesToAssign = Math.min(
          Math.floor(availableQuota / useQuota),
          remainingBoxes.length,
        );
        assignedBoxes = remainingBoxes.slice(0, boxesToAssign);
        availableQuota -= boxesToAssign * useQuota;
        remainingBoxes = remainingBoxes.slice(boxesToAssign);
      }

      // Assign items next
      if (remainingItems.length > 0 && availableQuota > 0) {
        const itemsToAssign = Math.min(availableQuota, remainingItems.length);
        assignedItems = remainingItems.slice(0, itemsToAssign);
        availableQuota -= itemsToAssign;
        remainingItems = remainingItems.slice(itemsToAssign);
      }

      return {
        ...aisle,
        box: assignedBoxes.length ? assignedBoxes : [],
        items: assignedItems.length ? assignedItems : [],
      };
    });

    setDigitalBox(_.uniqBy(assignedAisles, 'id'));
  };

  const getImage = (name, step) => {
    if (step > activeStep) return StepIcon[`${name}Inactive`];
    return StepIcon[`${name}Active`];
  };

  const renderSubmitText = () => {
    switch (activeStep) {
      case 1: return 'Proceed Request';
      case 3: if (req.status === 'Pickup Already Confirm by Customer' || req.status === 'Arrived at Warehouse') {
        return 'Next';
      } return '';
      case 4: return 'Verify Item';
      case 5: return 'Completed Request';
      default: return 'Next';
    }
  };

  const wantToContinue = () => {
    switch (activeStep) {
      case 1: openConfirmation({
        title: 'Proceed Request Order',
        desc: 'You will proceed this request order to the next step! Make sure you read all the request detail information',
        confirmText: 'Confirm Proceed',
        handleAgree: () => {
          setActiveStep(2);
          closeConfirmation();
        },
        handleClose: () => {
          closeConfirmation();
        },
        direction: 'row-reverse',
        maxWidth: 'xs',
      });
        break;
      case 2: if (!choosenCourier.id) {
        openAlert({
          message: 'Please choose a courier!',
          severity: 'error',
        });
        return;
      }
        openConfirmation({
          title: 'Confirm Courier Assigning',
          desc: 'You will assign courier to process this request order',
          confirmText: 'Confirm Assign',
          handleAgree: async () => {
            await handleAssignCourier();
            closeConfirmation();
          },
          handleClose: () => {
            closeConfirmation();
          },
          direction: 'row-reverse',
          maxWidth: 'xs',
        });
        break;
      case 3: openConfirmation({
        title: 'Accepting Package Confirmation?',
        desc: 'Are you sure has been received package from selected courier?',
        confirmText: 'Confirm Accepted',
        handleAgree: async () => {
          setActiveStep(4);
          setHideTH(true);
          closeConfirmation();
        },
        handleClose: () => {
          closeConfirmation();
        },
        direction: 'row-reverse',
        maxWidth: 'xs',
      });
        break;
      case 4: handleAssignItemsToBox();
        break;
      case 5: openConfirmation({
        title: 'Completing Request?',
        desc: 'Recheck again all the request detail before completing the request!',
        confirmText: 'Confirm',
        handleAgree: async () => {
          updateStatusAsDone();
          closeConfirmation();
        },
        handleClose: () => {
          closeConfirmation();
        },
        direction: 'row-reverse',
        maxWidth: 'xs',
      });
        break;
      default: setActiveStep(1);
    }
  };

  const wantToCancel = () => {
    setCancelStep(1);
  };

  const cancelOrderSeq = {
    0: {
      title: 'Decline Request Order?',
      desc: 'You will declined this request order from customer! Are you sure?',
      agree: 'Confirm Decline',
      next: null,
    },
    1: {
      title: 'Decline Request Order?',
      desc: 'You will declined this request order from customer! Are you sure?',
      agree: 'Confirm Decline',
      next: () => setCancelStep(2),
    },
    2: {
      title: 'Decline Request Order Note',
      desc: (
        <div>
          <div>Leave a note for declined request order. We will inform our customer.</div>
          <label className="mt-3">Notes</label>
          <TextField
            fullWidth
            placeholder="Write here..."
            value={cancelNotes}
            onChange={(e) => setCancelNotes(e.target.value)}
            name="notes"
          />
        </div>
      ),
      agree: 'Send Note',
      next: async () => {
        await handleDecline();
        setCancelStep(0);
      },
    },
  };

  const renderCourierAssigning = () => (
    <Box
      boxShadow="2px 3px 6px rgba(0, 0, 0, 0.04), 0px 2px 8px rgba(0, 0, 0, 0.02)"
      border="1px solid #F5F6F8"
      bgcolor="#FDFDFD"
      borderRadius="10px"
      p={3}
      mt={3}
    >
      <Box fontWeight="700">Assigning Courier</Box>
      <Box mt={2}>
        <Box display="flex" gap={3}>
          <Box flex={1}>
            <label>Choose courier</label>
            <Autocomplete
              autoComplete
              disabled={activeStep !== 2}
              options={couriers}
              value={choosenCourier}
              onChange={(e, newVal) => setChoosenCourier({
                ...newVal,
                id: newVal.courier?.id,
                profile_picture: newVal.courier?.profile_picture,
                phone_number: newVal.courier?.phone_number,
              })}
              getOptionLabel={(opt) => opt.courier_name || ''}
              renderOption={(optProp, opt) => (
                <Box sx={{ px: 2, py: 1 }} {...optProp} className="flex gap-8 align-items-center link" style={{ gap: '8px !important' }} key={opt.courier?.id}>
                  <img src={opt.courier?.profile_picture} alt="profile_pict" width="40" height="40" className="courier-opt" style={{ borderRadius: 50 }} />
                  <div>
                    <div className="weight-700">{opt.courier_name}</div>
                    <div className="s-14 mute">{opt.courier?.id}</div>
                  </div>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Choose courier"
                  InputProps={{
                    ...params.InputProps,
                  }}
                  size="small"
                />
              )}
            />
            <div className="s-12 mute mt-2">
              <strong>{couriers.length}</strong>
              {' '}
              Available couriers
            </div>
          </Box>
          <Box flex={1} display={choosenCourier.id ? 'block' : 'none'}>
            <Box fontWeight="700" fontSize={14}>Courier Information</Box>
            <Box display="flex" mt={1} gap={2}>
              <Box flex={1.5} display="flex" gap={2}>
                <img src={choosenCourier.profile_picture} alt="courier" width="32" height="32" className="circle cover" />
                <Box>
                  <Box className="s-14 mute">Courier Name</Box>
                  <Box mt={0.5}>
                    <Box fontWeight="700" fontSize={14}>{choosenCourier.courier_name || choosenCourier.front_name}</Box>
                  </Box>
                </Box>
              </Box>
              <Box flex={1}>
                <Box className="s-14 mute">Phone Number</Box>
                <Box mt={0.5}>
                  <Box fontWeight="700" fontSize={14}>{choosenCourier.phone_number}</Box>
                </Box>
              </Box>
              <Box flex={1}>
                <Box className="s-14 mute">Courier ID</Box>
                <Box mt={0.5}>
                  <Box fontWeight="700" fontSize={14}>
                    {choosenCourier.id?.slice(0, 7)}
                    ...
                  </Box>
                </Box>
              </Box>
            </Box>
            {req.status === 'Waiting for Courier Acceptance' && activeStep !== 2 && (
              <div className="primary-green link s-14 weight-600 mt-2" onClick={() => setActiveStep(2)}>Change Courier</div>
            )}
          </Box>
        </Box>
      </Box>
      <Divider sx={{ my: 3 }} />

      <Box sx={{
        borderRadius: 2,
        border: '1px solid #DADADA',
        p: 2,
        display: req.driver ? 'block' : 'none',
      }}
      >
        <div className="flex space-between link" onClick={() => setHideTH(!hideTrackHistory)}>
          <Box fontWeight={700}>Courier Track</Box>
          {hideTrackHistory
            ? <ExpandMoreRounded fontSize="medium" />
            : <ExpandLessRounded fontSize="medium" />}
        </div>
        <div hidden={hideTrackHistory}>
          <div className="stepper mt-3">
            {req.tracking_history?.map((history, idx) => (
              <div className="flex gap-16 mt-2" key={history.timestamp}>
                <img src={idx === 0 ? Images.LinePointActive : Images.LinePoint} alt="line point" />
                <div className="flex space-between flex-1">
                  <div>
                    <div className="weight-700 mt-1 s-16">{history.title}</div>
                    <div className="s-14 mute mt-1">{history.description || '-'}</div>
                  </div>
                  <div className="s-14 mute">
                    {moment(new Date(history.timestamp)).format('HH.mm')}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Box>
    </Box>
  );

  return (
    <Elements.AdminPageBase navigate={navigate}>
      <div className={classes.root}>
        {isLoading && <LinearProgress color="inherit" />}
        <Box fontWeight="700" fontSize={18} mt={2}>Detail Request Order</Box>
        <Box mt={4} className="flex align-items-center flex-center" gap={2}>
          <div className="center">
            <img height="40" width="40" src={getImage('RequestInformation', 1)} alt="Request in order data" />
            <div className={`mt-1 s-14 ${activeStep === 1 && 'weight-700 primary-green'}`}>Request Information</div>
          </div>
          <img src={Images.LongDivider} alt="LongDivider" />
          <div className="center">
            <img height="40" width="40" src={getImage('Courier', 2)} alt="Courier assign" />
            <div className={`mt-1 s-14 ${activeStep === 2 && 'weight-700 primary-green'}`}>Assigning Courier</div>
          </div>
          <img src={Images.LongDivider} alt="LongDivider" />
          <div className="center">
            <img height="40" width="40" src={getImage('TrackCourier', 3)} alt="Courier Track" />
            <div className={`mt-1 s-14 ${activeStep === 3 && 'weight-700 primary-green'}`}>Track Courier</div>
          </div>
          <img src={Images.LongDivider} alt="LongDivider" />
          <div className="center">
            <img height="40" width="40" src={getImage('WarehouseValidating', 4)} alt="Warehouse validate" />
            <div className={`mt-1 s-14 ${activeStep === 4 && 'weight-700 primary-green'}`}>Warehouse Validating</div>
          </div>
          <img src={Images.LongDivider} alt="LongDivider" />
          <div className="center">
            <img height="40" width="40" src={getImage('ConfirmRequest', 5)} alt="Confirmation" />
            <div className={`mt-1 s-14 ${activeStep === 5 && 'weight-700 primary-green'}`}>Confirm Request</div>
          </div>
        </Box>
        <Box
          boxShadow="2px 3px 6px rgba(0, 0, 0, 0.04), 0px 2px 8px rgba(0, 0, 0, 0.02)"
          border="1px solid #F5F6F8"
          bgcolor="#FDFDFD"
          borderRadius="10px"
          p={3}
          mt={4}
        >
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Box display="inline-flex" flexDirection="column" alignItems="center">
              <img src={Images.OenoIn} alt="request in" width="28" height="28" />
              <div className="weight-600 s-12 mt-1 primary-green">
                Request In
              </div>
            </Box>
            <Box textAlign="right">
              <Box display="inline-flex">
                <Box bgcolor="#D9EDFC" fontSize={14} fontWeight="700" color="#2391D9" py="2px" px={1} borderRadius="4px">{req.status}</Box>
              </Box>
              <Box fontWeight="600" mt={1}>{req.id}</Box>
            </Box>
          </Box>
          <Divider sx={{ my: 3 }} />
          <Box display="flex">
            <Box flex={1}>
              <Box className="s-14 mute">Request Service</Box>
              <Box
                display="flex"
                gap={0.5}
                mt={1}
                alignItems="center"
              >
                <img alt="delivery" width="24" height="24" src={Images.IconDelivery} />
                <Box fontWeight="700" fontSize={14}>Regular Pickup</Box>
              </Box>
            </Box>
            <Box flex={1}>
              <Box className="s-14 mute">Paid Status</Box>
              <Box mt={1}>
                <Box fontWeight="700" fontSize={14}>{req?.payment_status}</Box>
              </Box>
            </Box>
            <Box flex={1}>
              <Box className="s-14 mute">Pickup Date</Box>
              <Box mt={1}>
                <Box fontWeight="700" fontSize={14}>{moment(req.pickup_date).format('DD MMM YYYY, HH.mm')}</Box>
              </Box>
            </Box>
          </Box>
          <Divider sx={{ my: 3 }} />
          <Box>
            <Box fontWeight="700">Pickup Details</Box>
            <Box display="flex" mt={3} gap={4}>
              <Box bgcolor="#F5F6F8" border="1px solid #E5E5E5" borderRadius="5px" p={2} flex={1}>
                <Stack spacing={2} direction="column">
                  <Box>
                    <Box className="s-14 mute">Customer Name</Box>
                    <Box mt={0.5}>
                      <Box fontWeight="700" fontSize={14}>{req.pickup_address?.name}</Box>
                    </Box>
                  </Box>
                  <Box>
                    <Box className="s-14 mute">Phone Number</Box>
                    <Box mt={0.5}>
                      <Box fontWeight="700" fontSize={14}>
                        +
                        {req.pickup_address?.phone_number}
                      </Box>
                    </Box>
                  </Box>
                  <Box>
                    <Box className="s-14 mute">Pickup Address</Box>
                    <Box mt={0.5}>
                      <Box fontWeight="700" fontSize={14}>
                        {req.pickup_address?.address}
                        ,
                        {' '}
                        {req.pickup_address?.city}
                        ,
                        {' '}
                        {req.pickup_address?.province}
                        ,
                        {' '}
                        {req.pickup_address?.country}
                        ,
                        {' '}
                        {req.pickup_address?.postal_code}
                      </Box>
                    </Box>
                  </Box>
                </Stack>
              </Box>
              <Box py={2} flex={1}>
                <Box fontSize={14} fontWeight="700">Customer Information</Box>
                <Box mt={2}>
                  <Box className="s-14 mute">Customer ID</Box>
                  <Box mt={0.5}>
                    <Box fontWeight="700" fontSize={14}>{req.user?.id}</Box>
                  </Box>
                </Box>
                <Box mt={2}>
                  <Box className="s-14 mute">Subscription</Box>
                  <Box mt={0.5} display="flex" gap={1} alignItems="flex-start">
                    <img src={Images.IconSubscription} alt="subs" />
                    <Box>
                      <Box fontWeight="700" fontSize={14}>{req.user?.subscription}</Box>
                      <Box className="mute" fontSize={12} mt={0.5}>
                        Since
                        {' '}
                        {moment(req.user?.created_at).format('DD MMM YYYY, HH.mm')}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
          <Divider sx={{ my: 3 }} />
          <Box>
            <Box fontWeight="700">Item Details</Box>
            <Box display="flex" mt={3} gap={3}>
              <Box flex={1}>
                <Box className="s-14 mute">Total Items</Box>
                <Box mt={0.5}>
                  <Box fontWeight="700" fontSize={14}>
                    {getTotalItems(req)}
                  </Box>
                </Box>
              </Box>
              <Box flex={1}>
                <Box className="s-14 mute">Notes</Box>
                <Box mt={0.5}>
                  <Box fontWeight="700" fontSize={14}>{req.pickup_notes || req.box?.notes || '-'}</Box>
                </Box>
              </Box>
            </Box>
            <Box mt={3} display={activeStep === 4 ? 'block' : 'none'}>
              <BoxToValidate
                classes={classes}
                updateDigitalBox={handleDigitalBox}
              />
            </Box>
            <Box mt={3}>
              <Grid container spacing={2}>
                {req.items?.map((item, idx) => (
                  <Grid item sm={6} key={item.id}>
                    <Box bgcolor="#F5F6F8" borderRadius="5px" p={1.5}>
                      <Box fontSize={14} fontWeight="700">
                        Item
                        {' '}
                        {idx + 1}
                        {' '}
                        {digitalBox.find((i) => !!i.items.includes(item.id))
                        && <span className="primary-green s-12">- Assigned</span>}
                      </Box>
                      <Box mt={1}>
                        <Box className="s-14 mute">Wine Name</Box>
                        <Box mt={0.5}>
                          <Box fontWeight="700" fontSize={14}>{item.name}</Box>
                        </Box>
                      </Box>
                      <Box mt={1}>
                        <Box className="s-14 mute">Year of the Wine</Box>
                        <Box mt={0.5}>
                          <Box fontWeight="700" fontSize={14}>{item.year}</Box>
                        </Box>
                      </Box>
                      <Box mt={1}>
                        <Box className="s-14 mute">Wine Volume</Box>
                        <Box mt={0.5}>
                          <Box fontWeight="700" fontSize={14}>
                            {item.volume}
                          </Box>
                        </Box>
                      </Box>
                      <Box mt={1}>
                        <Box className="s-14 mute">Notes</Box>
                        <Box mt={0.5}>
                          <Box fontWeight="700" fontSize={14}>
                            {item.notes || '-'}
                          </Box>
                        </Box>
                      </Box>
                      <Box mt={1}>
                        <Box className="s-14 mute">Photo</Box>
                        <Box mt={0.5}>
                          {item.photo
                            ? (
                              <img
                                width="120"
                                height="120"
                                alt="product"
                                src={item.photo}
                                className="rounded"
                              />
                            ) : '-'}
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
            <Grid container spacing={2}>
              {
                req.box?.map((box, idx) => (
                  <Grid item sm={6} key={box.id}>
                    <Box bgcolor="#F5F6F8" borderRadius="5px" p={1.5} mt={3}>
                      <Box fontSize={14} fontWeight="700">
                        {box.name || `Box ${box.digital_box_id}`}
                        {' '}
                        {digitalBox.find((i) => !!i.box.includes(box.id))
                        && <span className="primary-green s-12">- Assigned</span>}
                      </Box>
                      <Box mt={1}>
                        <Box className="s-14 mute">Amount</Box>
                        <Box mt={0.5}>
                          <Box fontWeight="700" fontSize={14}>{box.amount}</Box>
                        </Box>
                      </Box>
                      <Box mt={1}>
                        <Box className="s-14 mute">Notes</Box>
                        <Box mt={0.5}>
                          <Box fontWeight="700" fontSize={14}>{box.notes || '-'}</Box>
                        </Box>
                      </Box>
                      <Box mt={1}>
                        <Box className="s-14 mute">Photo</Box>
                        <Box mt={0.5}>
                          {box.photo
                            ? (
                              <img
                                width="120"
                                height="120"
                                alt="product"
                                src={box.photo}
                                className="rounded"
                              />
                            ) : '-'}
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                ))
              }
            </Grid>
          </Box>
        </Box>

        {activeStep > 1 && renderCourierAssigning()}

        <Box className="flex space-between gap-16 mt-4">
          <Button
            sx={{ px: 4 }}
            variant="outlined"
            className="action btn-outlined"
            disabled={['Canceled', 'Admin Canceled'].includes(req?.status) || activeStep > 2}
            onClick={wantToCancel}
          >
            Decline Request
          </Button>
          <Button
            sx={{ px: 4, minWidth: 200 }}
            variant="contained"
            className="action btn-contained"
            onClick={wantToContinue}
            disabled={
              ['Canceled', 'Admin Canceled'].includes(req?.status)
              || (activeStep === 2 && !choosenCourier.id)
              || !renderSubmitText()
              || req.status === 'Done'
              || isLoading
            }
            startIcon={isLoading && (<CircularProgress size={18} sx={{ mr: 1, color: '#fff' }} />)}
          >
            {renderSubmitText() || 'Next'}
          </Button>
        </Box>
      </div>

      <Elements.ConfirmationModal
        open={Boolean(cancelStep)}
        handleClose={() => setCancelStep(0)}
        desc={cancelOrderSeq[cancelStep].desc}
        confirmText={cancelOrderSeq[cancelStep].agree}
        handleAgree={cancelOrderSeq[cancelStep].next}
        direction="row-reverse"
        maxWidth="xs"
      />
    </Elements.AdminPageBase>
  );
}

export function BoxToValidate({
  classes,
  updateDigitalBox,
  chooseOnly = false,
  open = false,
  handleClose = () => null,
}) {
  const { box, request } = useSelector((state) => state);

  const [chooseBox, setChooseBox] = useState(false);
  const [confirmRack, setConfirmRack] = useState(false);
  const [selectedRack, setSelectedRack] = useState([]);
  const dispatch = useDispatch();

  const checkBeforeOpenConfirmation = () => {
    if (selectedRack.length) {
      setConfirmRack(true);
    }
  };

  useEffect(() => {
    setChooseBox(open);
  }, [open]);

  const chooseRack = () => {
    if (selectedRack.length) {
      updateDigitalBox(selectedRack);
      setConfirmRack(false);
      setChooseBox(false);
      handleClose();
    }
  };

  function renderChooseStorage() {
    const boxes = request?.selectedRequest?.box;
    const item = request?.selectedRequest?.items;
    const total = (boxes?.length || 0) + (item?.length || 0);

    const handleCloseStorage = () => {
      if (selectedRack.length === 0) {
        setChooseBox(false);
        handleClose();
      } else {
        setSelectedRack((selected) => {
          const lastItem = selected.pop();
          dispatch(resetBoxColumn(lastItem.id, lastItem.rack_id, lastItem.column));
          return [...selected];
        });
      }
    };

    const handleSelectRack = (col, isAvailable, needstorage) => {
      if (isAvailable) {
        setSelectedRack((selected) => [...selected, col]);
        dispatch(setBoxColumn(col.id, col.rack_id, col.column, needstorage));
      }
    };

    const currentAssignment = selectedRack.length < boxes?.length ? 'Box' : 'Item';

    return (
      <Elements.ModalBase
        maxWidth="lg"
        open={chooseBox}
        confirmText={`Choose Aisle | ${currentAssignment}`}
        cancelText={selectedRack.length === 0 ? 'Cancel' : 'Back'}
        handleClose={handleCloseStorage}
        handleAgree={checkBeforeOpenConfirmation}
        disabled={selectedRack.length !== total}
        fullWidth
      >
        <Container maxWidth="md">
          <div className={classes.boxes}>
            <div className="flex space-between">
              <div>
                <div className="flex align-items-center">
                  <div>
                    <div className="weight-700">
                      {box.data?.available}
                      /
                      {box.data?.total}
                    </div>
                    <div className="s-14">available storages</div>
                  </div>
                </div>
              </div>

              <div className="flex align-self-center gap-16">
                <div className="flex align-items-center">
                  <div className="storage not_available" />
                  <div>
                    <div className="weight-700">Used storage</div>
                  </div>
                </div>
                <div className="flex align-items-center">
                  <div className="storage empty" />
                  <div>
                    <div className="weight-700">Empty storage</div>
                  </div>
                </div>
              </div>
            </div>
            <Grid container spacing={2} sx={{ mt: 2 }} justifyContent="center">
              {box.data?.data?.map((e, idx) => {
                const columnLength = e.row[0].column?.length;
                return (
                  <Grid item sm="auto" key={idx} sx={{ mb: 2 }}>
                    <div className="center">
                      <div className="s-18 weight-700">
                        Aisle
                        {' '}
                        {e.name}
                      </div>
                    </div>
                    <table className="mt-3">
                      <thead>
                        <tr>
                          <td aria-label="empty-td" />
                          {new Array(columnLength).fill(null).map((col, id) => (
                            <td className="s-14 center weight-700 mute" key={id}>{id + 1}</td>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {e.row.map((ev, i) => (
                          <tr key={i}>
                            <td className="s-14 weight-700 mute">{alphabets[i]}</td>
                            {ev.column.map((col) => {
                              let status;
                              let needstorage = 1;

                              if (selectedRack.length < boxes?.length) {
                                if (boxes[selectedRack.length]?.type === '1x3') needstorage = 3;
                                else if (boxes[selectedRack.length]?.type === '2x6') needstorage = 12;
                                else needstorage = 6;
                              }

                              if (col.available_slot < needstorage) status = 'not_available'; else status = col.status;

                              return (
                              // rack
                                <td key={col.id} aria-label={col.id}>
                                  <div
                                    className={`storage pointer ${status} ${selectedRack.find((rack) => rack.id === col.id) && 'selected'}`}
                                    onClick={() => (
                                      selectedRack.length < total ? handleSelectRack(col, status !== 'not_available', needstorage) : null
                                    )}
                                  />
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Grid>
                );
              })}
            </Grid>
          </div>
        </Container>
      </Elements.ModalBase>
    );
  }

  return (
    <Box>
      <Box display={chooseOnly ? 'none' : 'flex'} gap={1} alignItems="center">
        <Box flex={1}>
          <Box fontSize={14} fontWeight="600">
            Storage Site Location
          </Box>
          <TextField
            onClick={() => setChooseBox(true)}
            sx={{ mt: 0.5 }}
            fullWidth
            placeholder="Choose storage"
            size="small"
            value={`${selectedRack.map((rack, i) => `${rack.column}${rack.row}`).join(' | ')}`}
            InputProps={{
              endAdornment: (
                <IconButton sx={{ p: 0 }}>
                  <ChevronRightRounded fontSize="large" />
                </IconButton>
              ),
            }}
          />
        </Box>
        <Divider orientation="vertical" flexItem />
        <Box flex={1} pl={1}>
          <Box fontWeight="700" fontSize={14}>Notes</Box>
          <Box mt={1} fontSize={14} className="mute">
            1. Before select storage site make sure all item been verified.
            <br />
            2. Make sure be detailed when verifying item from courier.
            <br />
            3. 1 Box containt 6 items or 3 items or 12 items base on type.
          </Box>
        </Box>
      </Box>

      {
        renderChooseStorage()
      }

      <Elements.ConfirmationModal
        open={confirmRack}
        handleClose={() => setConfirmRack(false)}
        handleAgree={chooseRack}
        desc="Are you sure choose this aisle?"
      />
    </Box>
  );
}
